/**
 * Phase 20 — Guided estimate wizard: shared state machine.
 *
 * Derives step completion + the current/first-unanswered step from the
 * SAME conversation signals the existing "Almost there…" NextStepPanel
 * reads (selections, anchors, photos, snapshot). No new persistence —
 * progress is entirely derived. Web and mobile consume this identical
 * hook so the two surfaces cannot drift (Constitution surface-parity).
 */

import { useCallback, useMemo, useState } from "react";

import {
  buildSteps,
  stepIndex,
  type WizardStep,
  type WizardStepId,
} from "./steps";

/** Structured completion signals, lifted from existing estimate state. */
export interface WizardSignals {
  /** A project intent has been chosen (≥1 customer turn / room_type set). */
  hasIntent: boolean;
  /** A customer-described dimension is captured (a scale anchor / project area). */
  hasDimensions: boolean;
  /** At least one material selection exists. */
  hasFinish: boolean;
  /** At least one photo has been applied. */
  hasPhotos: boolean;
  /** An estimate/quote snapshot has been produced. */
  hasEstimate: boolean;
  /** The customer explicitly skipped the optional photos step (local UI state). */
  photosSkipped: boolean;
  /**
   * Phase 21 — the tenant has ≥1 active package for this estimate's room
   * type, so the project-type step belongs in the flow. When false the
   * flow is exactly the original five steps.
   */
  hasProjectTypes?: boolean;
  /** Phase 21 — a package has been applied to this estimate. */
  hasProjectType?: boolean;
}

export interface WizardDerived {
  completed: Set<WizardStepId>;
  firstUnansweredId: WizardStepId;
}

/** Pure completion rule for a single step (data-model.md §Completion rules). */
export function isStepComplete(id: WizardStepId, s: WizardSignals): boolean {
  switch (id) {
    case "intent":
      return s.hasIntent;
    case "project_type":
      // Phase 21 — complete once a package has been applied. Choosing
      // "Something else" applies none, and instead hands off to Stella,
      // which completes it the same way the "Other" intent does: by the
      // downstream signals the description produces.
      return Boolean(s.hasProjectType);
    case "dimensions":
      return s.hasDimensions;
    case "finish":
      return s.hasFinish;
    case "photos":
      // Optional: complete when a photo is applied OR the step was skipped.
      return s.hasPhotos || s.photosSkipped;
    case "estimate":
      return s.hasEstimate;
  }
}

/**
 * Pure derivation of completion + resume position from signals. Kept
 * separate from the hook so it is trivially unit-testable without React.
 */
export function deriveWizardState(s: WizardSignals): WizardDerived {
  const ids = buildSteps({ hasProjectTypes: s.hasProjectTypes }).map((x) => x.id);
  const completed = new Set<WizardStepId>();
  for (const id of ids) {
    if (isStepComplete(id, s)) completed.add(id);
  }
  // Resume at the first step that isn't complete; if everything is done,
  // rest on the final (estimate) step.
  const firstUnansweredId =
    ids.find((id) => !completed.has(id)) ?? ids[ids.length - 1];
  return { completed, firstUnansweredId };
}

export interface UseEstimateWizardArgs extends WizardSignals {
  /** A Stella turn is currently streaming — block advancing (FR-009). */
  streaming?: boolean;
  /** Read-only host (admin viewer) — no advanceable actions (FR-013). */
  readOnly?: boolean;
}

export interface UseEstimateWizardReturn {
  steps: readonly WizardStep[];
  currentStepId: WizardStepId;
  currentIndex: number;
  totalSteps: number;
  completed: Set<WizardStepId>;
  firstUnansweredId: WizardStepId;
  isComplete: (id: WizardStepId) => boolean;
  /** True when the current step is complete and the flow can move forward. */
  canAdvance: boolean;
  canGoBack: boolean;
  isLastStep: boolean;
  next: () => void;
  back: () => void;
  goTo: (id: WizardStepId) => void;
}

export function useEstimateWizard(
  args: UseEstimateWizardArgs,
): UseEstimateWizardReturn {
  const {
    streaming = false,
    readOnly = false,
    hasIntent,
    hasDimensions,
    hasFinish,
    hasPhotos,
    hasEstimate,
    photosSkipped,
    hasProjectTypes = false,
    hasProjectType = false,
  } = args;

  // Phase 21 — the flow's shape depends on context, so everything below
  // navigates THIS list rather than the module-level constant.
  const steps = useMemo(
    () => buildSteps({ hasProjectTypes }),
    [hasProjectTypes],
  );
  const stepIds = useMemo(() => steps.map((s) => s.id), [steps]);

  const { completed, firstUnansweredId } = useMemo(
    () =>
      deriveWizardState({
        hasIntent,
        hasDimensions,
        hasFinish,
        hasPhotos,
        hasEstimate,
        photosSkipped,
        hasProjectTypes,
        hasProjectType,
      }),
    [
      hasIntent,
      hasDimensions,
      hasFinish,
      hasPhotos,
      hasEstimate,
      photosSkipped,
      hasProjectTypes,
      hasProjectType,
    ],
  );

  // Explicit navigation override. Null → follow the derived resume point,
  // so a fresh mount opens at the first unanswered step (FR-008) and
  // auto-advances as signals complete until the customer navigates.
  const [override, setOverride] = useState<WizardStepId | null>(null);
  // An override can be stale after the step list changes (packages load
  // after first paint), so fall back to the derived resume point rather
  // than sitting on a step that is no longer in the flow.
  const overrideIsValid = override !== null && stepIds.includes(override);
  const currentStepId = overrideIsValid ? override : firstUnansweredId;
  const currentIndex = stepIndex(currentStepId, stepIds);
  const isLastStep = currentIndex === stepIds.length - 1;

  const isComplete = useCallback(
    (id: WizardStepId) => completed.has(id),
    [completed],
  );

  const canAdvance =
    !readOnly && !streaming && completed.has(currentStepId) && !isLastStep;
  const canGoBack = currentIndex > 0;

  const next = useCallback(() => {
    setOverride(stepIds[Math.min(currentIndex + 1, stepIds.length - 1)]);
  }, [currentIndex, stepIds]);

  const back = useCallback(() => {
    setOverride(stepIds[Math.max(currentIndex - 1, 0)]);
  }, [currentIndex, stepIds]);

  const goTo = useCallback((id: WizardStepId) => setOverride(id), []);

  return {
    steps,
    currentStepId,
    currentIndex,
    totalSteps: stepIds.length,
    completed,
    firstUnansweredId,
    isComplete,
    canAdvance,
    canGoBack,
    isLastStep,
    next,
    back,
    goTo,
  };
}
